import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Shield, ChevronLeft, ChevronRight, Search, Filter, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE as API } from '../../config';

const CATEGORY_COLORS = {
  auth: 'bg-blue-500/20 text-blue-300',
  claim: 'bg-amber-500/20 text-amber-300',
  company: 'bg-emerald-500/20 text-emerald-300',
  user: 'bg-purple-500/20 text-purple-300',
  tenant: 'bg-cyan-500/20 text-cyan-300',
  document: 'bg-pink-500/20 text-pink-300',
  note: 'bg-orange-500/20 text-orange-300',
};

const CATEGORIES = ['auth', 'claim', 'company', 'user', 'tenant', 'document', 'note'];

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function DevAuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteTenant, setDeleteTenant] = useState('');
  const [deleteAge, setDeleteAge] = useState('');
  const [customDays, setCustomDays] = useState('');
  const [category, setCategory] = useState('');
  const [searchAction, setSearchAction] = useState('');
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const limit = 30;

  useEffect(() => {
    axios.get(`${API}/dev/tenants`, { withCredentials: true }).then(r => setTenants(r.data)).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (category) params.append('category', category);
      if (searchAction) params.append('action', searchAction);
      if (tenantFilter) params.append('tenant_id', tenantFilter);
      const res = await axios.get(`${API}/dev/audit-logs?${params}`, { withCredentials: true });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to fetch audit logs', err);
    } finally {
      setLoading(false);
    }
  }, [page, category, searchAction, tenantFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const deleteLog = async (logId) => {
    setDeleting(logId);
    try {
      await axios.delete(`${API}/dev/audit-logs/${logId}`, { withCredentials: true });
      toast.success('Log entry deleted');
      fetchLogs();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(null); }
  };

  const deleteFilteredLogs = async () => {
    const days = deleteAge === 'custom' ? parseInt(customDays) : deleteAge ? parseInt(deleteAge) : null;
    const parts = [];
    if (deleteTenant) parts.push(`tenant "${tenants.find(t => t.tenant_id === deleteTenant)?.name || deleteTenant}"`);
    if (days) parts.push(`older than ${days} days`);
    const desc = parts.length ? parts.join(' and ') : 'ALL audit logs';
    if (!window.confirm(`Delete ${desc}? This cannot be undone.`)) return;
    setDeleting('bulk');
    try {
      const params = new URLSearchParams();
      if (deleteTenant) params.append('tenant_id', deleteTenant);
      if (days) params.append('older_than_days', days);
      const res = await axios.delete(`${API}/dev/audit-logs?${params}`, { withCredentials: true });
      toast.success(res.data.message);
      setDeleteTenant(''); setDeleteAge(''); setCustomDays('');
      fetchLogs();
    } catch { toast.error('Failed to delete'); }
    finally { setDeleting(null); }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-8" data-testid="dev-audit-log-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-emerald-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Audit Log</h1>
            <p className="text-sm text-slate-400">{total} total events across all tenants</p>
          </div>
        </div>
      </div>

      {/* Delete Options */}
      {total > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-5 bg-slate-900 rounded-lg border border-red-900/30 p-3" data-testid="delete-options-bar">
          <Trash2 className="w-4 h-4 text-red-500" />
          <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Delete</span>
          <Select value={deleteTenant || 'all'} onValueChange={(v) => setDeleteTenant(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48 h-8 bg-slate-800 border-slate-700 text-slate-300 text-sm" data-testid="delete-tenant-select">
              <SelectValue placeholder="All Tenants" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tenants</SelectItem>
              {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={deleteAge || 'none'} onValueChange={(v) => { setDeleteAge(v === 'none' ? '' : v); if (v !== 'custom') setCustomDays(''); }}>
            <SelectTrigger className="w-44 h-8 bg-slate-800 border-slate-700 text-slate-300 text-sm" data-testid="delete-age-select">
              <SelectValue placeholder="Any Age" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Any Age</SelectItem>
              <SelectItem value="30">Older than 30 days</SelectItem>
              <SelectItem value="60">Older than 60 days</SelectItem>
              <SelectItem value="90">Older than 90 days</SelectItem>
              <SelectItem value="120">Older than 120 days</SelectItem>
              <SelectItem value="custom">Custom days...</SelectItem>
            </SelectContent>
          </Select>
          {deleteAge === 'custom' && (
            <Input
              type="number"
              min="1"
              placeholder="Days"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              className="w-24 h-8 bg-slate-800 border-slate-700 text-slate-300 text-sm"
              data-testid="delete-custom-days"
            />
          )}
          <Button
            variant="outline"
            size="sm"
            className="border-red-800 text-red-400 hover:bg-red-900/30 hover:text-red-300 h-8 text-sm"
            onClick={deleteFilteredLogs}
            disabled={deleting === 'bulk' || (deleteAge === 'custom' && !customDays)}
            data-testid="delete-audit-logs-btn"
          >
            {deleting === 'bulk' ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5 bg-slate-900 rounded-lg border border-slate-800 p-3" data-testid="dev-audit-filters">
        <Filter className="w-4 h-4 text-slate-500" />
        <Select value={tenantFilter || 'all'} onValueChange={(v) => { setTenantFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-48 h-9 bg-slate-800 border-slate-700 text-slate-300" data-testid="dev-audit-filter-tenant">
            <SelectValue placeholder="All Tenants" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tenants</SelectItem>
            {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category || 'all'} onValueChange={(v) => { setCategory(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-40 h-9 bg-slate-800 border-slate-700 text-slate-300" data-testid="dev-audit-filter-category">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <Input
            placeholder="Search actions..."
            value={searchAction}
            onChange={(e) => { setSearchAction(e.target.value); setPage(1); }}
            className="pl-8 w-52 h-9 bg-slate-800 border-slate-700 text-slate-300 placeholder:text-slate-500"
            data-testid="dev-audit-filter-search"
          />
        </div>
        {(category || searchAction || tenantFilter) && (
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-slate-200" onClick={() => { setCategory(''); setSearchAction(''); setTenantFilter(''); setPage(1); }} data-testid="dev-audit-filter-clear">
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
        <table className="w-full" data-testid="dev-audit-table">
          <thead>
            <tr className="border-b border-slate-800">
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Time</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Category</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Action</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Description</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">User</th>
              <th className="text-left text-xs text-slate-500 uppercase tracking-wider px-4 py-3">Tenant</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {loading ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500">Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500">No audit logs found</td></tr>
            ) : logs.map((log) => (
              <tr key={log.log_id} className="hover:bg-slate-800/40 transition-colors" data-testid={`dev-audit-row-${log.log_id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock className="w-3 h-3" />
                    {formatDate(log.created_at)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${CATEGORY_COLORS[log.category] || 'bg-slate-700 text-slate-400'}`}>
                    {log.category}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded text-slate-300">{log.action}</code>
                </td>
                <td className="px-4 py-3 text-sm text-slate-300 max-w-xs truncate">{log.description}</td>
                <td className="px-4 py-3">
                  <p className="text-xs text-slate-300">{log.user_name || '-'}</p>
                  {log.user_email && <p className="text-[10px] text-slate-500">{log.user_email}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{log.tenant_id ? tenants.find(t => t.tenant_id === log.tenant_id)?.name || log.tenant_id : '-'}</td>
                <td className="px-2 py-3">
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteLog(log.log_id); }}
                    disabled={deleting === log.log_id}
                    className="p-1 text-slate-600 hover:text-red-400 transition-colors"
                    data-testid={`delete-audit-log-${log.log_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4" data-testid="dev-audit-pagination">
          <p className="text-xs text-slate-500">Page {page} of {totalPages} ({total} events)</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="dev-audit-prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="dev-audit-next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
